import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Drawer,
  IconButton,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Alert,
  Snackbar,
  CircularProgress,
  Divider,
  Checkbox,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Radio,
} from "@mui/material";
import { 
  Close as CloseIcon, 
  Save as SaveIcon, 
  CheckCircle as CheckCircleIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  Folder as TemplateIcon
} from "@mui/icons-material";
import {
  getToolMapping,
  saveToolMapping,
  updateToolMapping,
  deleteToolMapping,
} from "../../services/webServices";
import {
  getAllJiraFields,
  getADOAvailableFields,
  getTestCaseFieldsFromTestRail,
  getqtestAllFields,
  getCaseTemplatesFromTestRail, 
} from "../../services/webServices";

const ImportFields = {
  "Bug": [
    { key: "Title", label: "Title*" },
    { key: "Description", label: "Description" },
    { key: "Environment_Details", label: "Environment Details" },
    { key: "Steps_To_Reproduce", label: "Steps to Reproduce" },
    { key: "Expected_Result", label: "Expected Result" },
    { key: "Actual_Result", label: "Actual Result" },
    { key: "Additional_Information", label: "Additional Information" },
    { key: "Impact", label: "Impact" },
  ],
  "Test Cases": [
    { key: "Test Case Title", label: "Test Case Title*" },
    { key: "Priority", label: "Priority*" },
    { key: "Category", label: "Category*" },
    { key: "Test Objective", label: "Test Objective" },
    { key: "Pre-conditions", label: "Pre-conditions" },
    { key: "Test Steps", label: "Test Steps" },
    { key: "Expected Result", label: "Expected Result" },
    { key: "Post-conditions", label: "Post-conditions" },
  ],
  "Story": [
    { key: "Title", label: "Title*" },
    { key: "Description", label: "Description" },
    { key: "Acceptance Criteria", label: "Acceptance Criteria" },
    { key: "Additional Information", label: "Additional Information" },
  ],
};

const ExportFields = {
  "Test Cases": [
    { key: "Test Case Title", label: "Test Case Title*" },
    { key: "Priority", label: "Priority*" },
    { key: "Category", label: "Category*" },
    { key: "Test Objective", label: "Test Objective" },
    { key: "Pre-conditions", label: "Pre-conditions" },
    { key: "Test Steps", label: "Test Steps" },
    { key: "Expected Result", label: "Expected Result" },
    { key: "Post-conditions", label: "Post-conditions" },
  ],
  "Story": [
    { key: "Title", label: "Title*" },
    { key: "Description", label: "Description" },
    { key: "Acceptance Criteria", label: "Acceptance Criteria" },
    { key: "Additional Information", label: "Additional Information" },
  ],
  "Bug": [
    { key: "Title", label: "Title*" },
    { key: "Description", label: "Description" },
    { key: "Steps_To_Reproduce", label: "Steps to Reproduce" },
    {
      key: "Expected_Vs_Actual_Behavior",
      label: "Expected Vs Actual Behavior",
    },
    { key: "Evidences_TBD", label: "Evidence" },
    { key: "Business_Impact", label: "Business Impact" },
    { key: "Technical_Impact", label: "Technical Impact" },
    { key: "Workaround", label: "Workaround" },
    { key: "Severity_Level", label: "Severity Level" },
    {
      key: "Possible_Causes_For_Defect_Leakage",
      label: "Possible Causes for Defect Leakage",
    },
    { key: "Ideal_Test_Case", label: "Ideal Test Case" },
    {
      key: "Possible_Causes_For_Dev_Team",
      label: "Possible Causes for Dev Team",
    },
    {
      key: "Recommended_Fixes_For_Dev_Team",
      label: "Recommended Fixes for Dev Team",
    },
    { key: "Similar_Bugs", label: "Similar Bugs" },
    { key: "Additional_Information", label: "Additional Information" },
    {
      key: "Validation_And_Verification_Retesting",
      label: "Validation And Verification Retesting",
    },
  ],
};

const ToolMapping = ({
  open,
  onClose,
  onCloseAll,
  selectedApp,
  selectedConnection,
  selectedProject,
  selectedTemplate: propSelectedTemplate, 
  connectionData,
}) => {
  const [selectedIssueType, setSelectedIssueType] = useState("Story");
  const [mappingName, setMappingName] = useState("");
  const [exportMappingName, setExportMappingName] = useState("");
  const [importFieldMappings, setImportFieldMappings] = useState({});
  const [exportFieldMappings, setExportFieldMappings] = useState({});
  const [keepSameForExport, setKeepSameForExport] = useState(false);
  const [existingImportMapping, setExistingImportMapping] = useState(null);
  const [existingExportMapping, setExistingExportMapping] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [fetchingFields, setFetchingFields] = useState(false);
  const [availableTargetFields, setAvailableTargetFields] = useState([]);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [successDialogOpen, setSuccessDialogOpen] = useState(false);
  const [viewMode, setViewMode] = useState(false);
  const [hasExistingMapping, setHasExistingMapping] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteType, setDeleteType] = useState(null);
  
  const [deleteSuccessDialogOpen, setDeleteSuccessDialogOpen] = useState(false);
  const [deleteErrorDialogOpen, setDeleteErrorDialogOpen] = useState(false);
  const [deleteMessage, setDeleteMessage] = useState("");
  // New state for template selection
  const [templateDrawerOpen, setTemplateDrawerOpen] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(propSelectedTemplate); 
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [templateError, setTemplateError] = useState(null);

  const importFields = ImportFields[selectedIssueType] || [];
  const exportFields = ExportFields[selectedIssueType] || [];

  const showKeepSameCheckbox =
    selectedIssueType === "Test Cases" || selectedIssueType === "Story";

  const getAvailableIssueTypes = (selectedApp) => {
    const appName = selectedApp?.toLowerCase();
    
    switch (appName) {
      case 'jira':
        return ["Story", "Bug"];
      case 'testrail':
        return ["Test Cases"];
      case 'qtest':
        return ["Test Cases", "Bug"];
      case 'azure':
      case 'ado':
        return ["Test Cases", "Story", "Bug"];
      default:
        return ["Test Cases", "Story", "Bug"];
    }
  };

  const availableIssueTypes = getAvailableIssueTypes(selectedApp);

  const resetComponentState = () => {
    const defaultIssueType = getAvailableIssueTypes(selectedApp)[0] || "Story";
    setSelectedIssueType(defaultIssueType);
    setMappingName("");
    setExportMappingName("");
    setImportFieldMappings({});
    setExportFieldMappings({});
    setKeepSameForExport(false);
    setExistingImportMapping(null);
    setExistingExportMapping(null);
    setAvailableTargetFields([]);
    setSuccessDialogOpen(false);
    setViewMode(false);
    setHasExistingMapping(false);
    setDeleteDialogOpen(false);
    setDeleteType(null);
    // Reset template state but keep prop value
    setSelectedTemplate(propSelectedTemplate);
    setTemplates([]);
    setTemplateError(null);
  };

  const resetMappingFields = () => {
    setMappingName("");
    setExportMappingName("");
    setImportFieldMappings({});
    setExportFieldMappings({});
    setKeepSameForExport(false);
    setAvailableTargetFields([]);
    setViewMode(false);
    setDeleteDialogOpen(false);
    setDeleteType(null);
  };

  // Update internal selectedTemplate when prop changes
  useEffect(() => {
    setSelectedTemplate(propSelectedTemplate);
  }, [propSelectedTemplate]);

  useEffect(() => {
    if (open) {
      resetComponentState();
    }
  }, [open, selectedApp, selectedConnection, selectedProject]);

  useEffect(() => {
    if (
      open &&
      selectedApp &&
      selectedConnection &&
      selectedProject &&
      selectedIssueType
    ) {
      fetchAvailableFields();
      fetchExistingMapping();
    }
  }, [open, selectedApp, selectedConnection, selectedProject, selectedIssueType, selectedTemplate]); // Add selectedTemplate to dependencies

  useEffect(() => {
    if (!open) {
      resetComponentState();
    }
  }, [open]);

  const handleIssueTypeChange = (newIssueType) => {
    setSelectedIssueType(newIssueType);
    setMappingName("");
    setExportMappingName("");
    setImportFieldMappings({});
    setExportFieldMappings({});
    setKeepSameForExport(false);
    setViewMode(false);
    setExistingImportMapping(null);
    setExistingExportMapping(null);
    setHasExistingMapping(false);
    // Clear fields when issue type changes
    setAvailableTargetFields([]);
  };

  useEffect(() => {
    if (selectedApp && availableIssueTypes.length > 0) {
      if (!availableIssueTypes.includes(selectedIssueType)) {
        setSelectedIssueType(availableIssueTypes[0]);
        resetMappingFields();
      }
    }
  }, [selectedApp, availableIssueTypes, selectedIssueType]);

  // Fetch templates for TestRail
  const fetchTemplates = async () => {
    if (!selectedProject || !selectedProject.id) {
      setTemplateError("No project selected");
      return;
    }

    setLoadingTemplates(true);
    setTemplateError(null);
    
    try {
      const response = await getCaseTemplatesFromTestRail(selectedProject.id, selectedConnection.fullConfig.config_id);
      const templatesData = response.data || [];
      
      if (Array.isArray(templatesData) && templatesData.length > 0) {
        setTemplates(templatesData);
        setTemplateDrawerOpen(true);
      } else {
        setTemplateError("No templates found for this TestRail project");
        showSnackbar("No templates found for this project", "warning");
      }
    } catch (error) {
      console.error('Error fetching TestRail templates:', error);
      setTemplateError("Failed to load templates. Please try again.");
      showSnackbar("Error loading templates", "error");
    } finally {
      setLoadingTemplates(false);
    }
  };

  const handleTemplateSelect = (template) => {
    setSelectedTemplate(template);
    setTemplateDrawerOpen(false);
    // Clear existing fields when template changes
    setAvailableTargetFields([]);
    setImportFieldMappings({});
    setExportFieldMappings({});
    
    // Show success message
    showSnackbar(`Template "${template.name}" selected`, "success");
  };

  const handleShowTemplates = () => {
    fetchTemplates();
  };

  const handleClearTemplate = () => {
    setSelectedTemplate(null);
    setAvailableTargetFields([]);
    setImportFieldMappings({});
    setExportFieldMappings({});
    showSnackbar("Template selection cleared", "info");
  };

  const fetchAvailableFields = async () => {
    if (!selectedApp || !selectedProject || !selectedIssueType) return;

    // For TestRail, we need a template to fetch fields
    if (selectedApp.toLowerCase() === 'testrail' && !selectedTemplate) {
      setAvailableTargetFields([]);
      return;
    }

    setFetchingFields(true);
    try {
      let fields = [];

      switch (selectedApp.toLowerCase()) {
        case 'jira':
          const jiraPayload = {
            projectName: selectedProject.key || selectedProject.name,
            issueType: selectedIssueType,
            config_id: selectedConnection
          };
          const jiraResponse = await getAllJiraFields(jiraPayload);
          fields = jiraResponse.data || [];
          break;

        case 'azure':
        case 'ado':
          const adoPayload = {
            projectName: selectedProject.name || selectedProject.displayName,
            issueType: selectedIssueType,
            config_id: selectedConnection
          };
          const adoResponse = await getADOAvailableFields(adoPayload);
          fields = adoResponse.data || [];
          break;

        case 'testrail':
          console.log(selectedProject)
          if (selectedTemplate && selectedTemplate.id && selectedProject.id) {
            const testRailResponse = await getTestCaseFieldsFromTestRail(
              selectedTemplate.id, // template_id first
              selectedProject.id,   // project_id second
              selectedConnection
            );
            fields = testRailResponse.data?.fields || [];
          }
          break;

        case 'qtest':
          const issueTypeToApiType = {
            "Test Cases": "test-cases",
            "Bug": "defects",
          };
          
          const normalizedIssueType = issueTypeToApiType[selectedIssueType];     
          const qtestResponse = await getqtestAllFields(
            selectedProject.id,
            normalizedIssueType,
            selectedConnection
          );
          fields = qtestResponse.fields || [];
          break;

        default:
          console.warn(`Unsupported tool: ${selectedApp}`);
          fields = [];
      }

      const transformedFields = fields.map(field => ({
        name: field.name || field.label,
        key: field.key || field.id || field.name,
        type: field.type || 'text'
      }));

      setAvailableTargetFields(transformedFields);
    } catch (error) {
      console.error("Error fetching available fields:", error);
      setAvailableTargetFields([]);
      showSnackbar("Error fetching available fields from the tool", "error");
    } finally {
      setFetchingFields(false);
    }
  };

  useEffect(() => {
    if (keepSameForExport && importFields.length > 0) {
      const syncedExportMappings = {};
      importFields.forEach((field) => {
        if (importFieldMappings[field.key]) {
          syncedExportMappings[field.key] = importFieldMappings[field.key];
        }
      });
      setExportFieldMappings(syncedExportMappings);
    }
  }, [keepSameForExport, importFieldMappings, importFields]);

  const fetchExistingMapping = async () => {
    if (!selectedApp || !connectionData || !selectedProject || !selectedIssueType)
      return;

    setLoading(true);
    try {
      const projectName = selectedProject.displayName || selectedProject.name;
      
      const fetchParams = {
        tool: selectedApp,
        tool_url: connectionData.url,
        project_name: projectName.replace(/&/g, "%26"),
        issue_type: selectedIssueType,
      };

      // Fetch both import and export mappings independently
      const [importResponse, exportResponse] = await Promise.all([
        getToolMapping({ ...fetchParams, task_type: "import" }).catch(err => {
          console.log("Import mapping fetch error (might not exist):", err);
          return { data: null };
        }),
        getToolMapping({ ...fetchParams, task_type: "export" }).catch(err => {
          console.log("Export mapping fetch error (might not exist):", err);
          return { data: null };
        })
      ]);

      // Process import mapping
      let hasImportMapping = false;
      if (importResponse.data && importResponse.data.mapping && Object.keys(importResponse.data.mapping).length > 0) {
        hasImportMapping = true;
        setImportFieldMappings(importResponse.data.mapping);
        setMappingName(importResponse.data.mapping_name || "");
        
        const importMappingWithId = {
          ...importResponse.data,
          id: importResponse.data.id || importResponse.data.toolmapping_id || importResponse.data._id
        };
        setExistingImportMapping(importMappingWithId);
      } else {
        setImportFieldMappings({});
        setMappingName("");
        setExistingImportMapping(null);
      }

      // Process export mapping
      let hasExportMapping = false;
      if (exportResponse.data && exportResponse.data.mapping && Object.keys(exportResponse.data.mapping).length > 0) {
        hasExportMapping = true;
        setExportFieldMappings(exportResponse.data.mapping);
        setExportMappingName(exportResponse.data.mapping_name || "");
        
        const exportMappingWithId = {
          ...exportResponse.data,
          id: exportResponse.data.id || exportResponse.data.toolmapping_id || exportResponse.data._id
        };
        setExistingExportMapping(exportMappingWithId);
      } else {
        setExportFieldMappings({});
        setExportMappingName("");
        setExistingExportMapping(null);
      }

      const hasAnyMapping = hasImportMapping || hasExportMapping;
      setHasExistingMapping(hasAnyMapping);

    } catch (error) {
      console.error("Error fetching mapping:", error);
      setHasExistingMapping(false);
      setImportFieldMappings({});
      setExportFieldMappings({});
      setMappingName("");
      setExportMappingName("");
      setExistingImportMapping(null);
      setExistingExportMapping(null);
    } finally {
      setLoading(false);
    }
  };

  const handleImportFieldMappingChange = (fieldKey, mappedValue) => {
    const newImportMappings = {
      ...importFieldMappings,
      [fieldKey]: mappedValue,
    };
    setImportFieldMappings(newImportMappings);

    if (keepSameForExport) {
      setExportFieldMappings((prev) => ({
        ...prev,
        [fieldKey]: mappedValue,
      }));
    }
  };

  const handleExportFieldMappingChange = (fieldKey, mappedValue) => {
    setExportFieldMappings((prev) => ({
      ...prev,
      [fieldKey]: mappedValue,
    }));
  };

  const handleKeepSameChange = (checked) => {
    setKeepSameForExport(checked);
    if (checked) {
      setExportFieldMappings({ ...importFieldMappings });
      setExportMappingName(mappingName);
    } else {
      setExportFieldMappings({});
      setExportMappingName("");
    }
  };

  useEffect(() => {
    if (keepSameForExport) {
      setExportMappingName(mappingName);
    }
  }, [keepSameForExport, mappingName]);

  const handleSaveMapping = async () => {
    if (!mappingName.trim() || !selectedIssueType) {
      showSnackbar("Please enter a mapping name", "error");
      return;
    }

    if (!keepSameForExport && !exportMappingName.trim()) {
      showSnackbar("Please enter export mapping name", "error");
      return;
    }

    // For TestRail, warn if no template is selected but allow saving
    if (selectedApp.toLowerCase() === 'testrail' && !selectedTemplate) {
      const proceed = window.confirm(
        "No template selected for TestRail. Fields will be limited. Do you want to continue?"
      );
      if (!proceed) return;
    }

    setSaving(true);
    try {
      const commonData = {
        tool: selectedApp,
        tool_url: connectionData.url,
project_name: selectedApp.toLowerCase() === 'testrail' && selectedTemplate 
    ? `${selectedProject.displayName || selectedProject.name}->${selectedTemplate.name}`
    : selectedProject.name || selectedProject.displayName,        issue_type: selectedIssueType,
        // Include template information for TestRail
        ...(selectedApp.toLowerCase() === 'testrail' && selectedTemplate && {
          template_id: selectedTemplate.id,
          template_name: selectedTemplate.name
        })
      };

      const completeImportMapping = {};
    importFields.forEach(field => {
      completeImportMapping[field.key] = importFieldMappings[field.key] || null;
    });

    // Create complete export mapping with null for unmapped fields
    const completeExportMapping = {};
    exportFields.forEach(field => {
      completeExportMapping[field.key] = exportFieldMappings[field.key] || null;
    });

      const importMappingData = {
        ...commonData,
        mapping_name: mappingName.trim(),
        mapping: completeImportMapping,
        task_type: "import",
      };

      const exportMappingData = {
        ...commonData,
        mapping_name: keepSameForExport
          ? mappingName.trim()
          : exportMappingName.trim(),
        mapping: completeExportMapping,
        task_type: "export",
      };

      let importResponse, exportResponse;

      // Handle import mapping
      if (existingImportMapping?.id && viewMode) {
        importResponse = await updateToolMapping({
          ...importMappingData,
          id: existingImportMapping.id
        });
      } else {
        importResponse = await saveToolMapping(importMappingData);
      }

      // Handle export mapping
      if (existingExportMapping?.id && viewMode) {
        exportResponse = await updateToolMapping({
          ...exportMappingData,
          id: existingExportMapping.id
        });
      } else {
        exportResponse = await saveToolMapping(exportMappingData);
      }

      setSuccessDialogOpen(true);
      setViewMode(false);
      
      setTimeout(() => {
        fetchExistingMapping();
      }, 500);
      
    } catch (error) {
      console.error("Error saving mapping:", error);
      showSnackbar(
        error.response?.data?.message || "Error saving mapping",
        "error"
      );
    } finally {
      setSaving(false);
    }
  };

 const handleDeleteMapping = async () => {
  try {
    if (deleteType === 'import' && existingImportMapping?.id) {
      const response = await deleteToolMapping(existingImportMapping.id);
      setDeleteMessage("Import mapping deleted successfully");
      setDeleteSuccessDialogOpen(true);
      
      setExistingImportMapping(null);
      setImportFieldMappings({});
      setMappingName("");
      
      setHasExistingMapping(!!existingExportMapping);
      
    } else if (deleteType === 'export' && existingExportMapping?.id) {
      const response = await deleteToolMapping(existingExportMapping.id);
      setDeleteMessage("Export mapping deleted successfully");
      setDeleteSuccessDialogOpen(true);
      
      setExistingExportMapping(null);
      setExportFieldMappings({});
      setExportMappingName("");
      
      setHasExistingMapping(!!existingImportMapping);
      
    } else {
      setDeleteMessage("No mapping found to delete");
      setDeleteErrorDialogOpen(true);
      setDeleteDialogOpen(false);
      setDeleteType(null);
      return;
    }

    setDeleteDialogOpen(false);
    setDeleteType(null);
    
    setTimeout(() => {
      fetchExistingMapping();
    }, 1000);
    
  } catch (error) {
    console.error("Error deleting mapping:", error);
    setDeleteMessage(error.response?.data?.message || "Error deleting mapping");
    setDeleteErrorDialogOpen(true);
    setDeleteDialogOpen(false);
    setDeleteType(null);
  }
};

  const handleViewMapping = () => {
    setViewMode(true);
  };

  const handleEditMapping = () => {
    setViewMode(false);
  };

  const handleCloseSuccessDialog = () => {
    setSuccessDialogOpen(false);
    resetComponentState();
    if (onCloseAll) {
      onCloseAll();
    } else {
      onClose();
    }
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleDrawerClose = () => {
    resetComponentState();
    onClose();
  };

  const handleDeleteClick = (type) => {
    setDeleteType(type);
    setDeleteDialogOpen(true);
  };

  // Template Selection Drawer Component
  const TemplateSelectionDrawer = () => (
    <Drawer
      anchor="right"
      open={templateDrawerOpen}
      onClose={() => setTemplateDrawerOpen(false)}
      sx={{
        "& .MuiDrawer-paper": {
          width: 400,
          boxSizing: "border-box",
        },
      }}
    >
      <Box sx={{ p: 3, height: "100%", display: "flex", flexDirection: "column" }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
          <Typography variant="h6">Select TestRail Template</Typography>
          <IconButton onClick={() => setTemplateDrawerOpen(false)}>
            <CloseIcon />
          </IconButton>
        </Box>

        {templateError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {templateError}
          </Alert>
        )}

        {loadingTemplates ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", flex: 1 }}>
            <CircularProgress />
            <Typography variant="body2" sx={{ ml: 2 }}>
              Loading templates...
            </Typography>
          </Box>
        ) : templates.length === 0 ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", flex: 1, flexDirection: "column" }}>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
              No templates available
            </Typography>
            <Typography variant="body2" color="text.secondary">
              This project doesn't have any case templates.
            </Typography>
          </Box>
        ) : (
          <List sx={{ flex: 1, overflowY: 'auto' }}>
            {templates.map((template) => (
              <ListItem
                key={template.id}
                button
                onClick={() => handleTemplateSelect(template)}
                sx={{
                  border: selectedTemplate?.id === template.id ? '2px solid #49A3FF' : '1px solid #e0e0e0',
                  borderRadius: 1,
                  mb: 1,
                  backgroundColor: selectedTemplate?.id === template.id ? '#f0f8ff' : 'white',
                }}
              >
                <ListItemIcon>
                  <Radio
                    checked={selectedTemplate?.id === template.id}
                    onChange={() => handleTemplateSelect(template)}
                    value={template.id}
                  />
                </ListItemIcon>
                <ListItemText 
                  primary={template.name} 
                  secondary={`ID: ${template.id}`}
                />
              </ListItem>
            ))}
          </List>
        )}

        <Box sx={{ mt: 'auto', pt: 2, borderTop: '1px solid #e0e0e0' }}>
          <Button
            fullWidth
            variant="outlined"
            onClick={() => setTemplateDrawerOpen(false)}
            sx={{ textTransform: 'none' }}
          >
            Cancel
          </Button>
        </Box>
      </Box>
    </Drawer>
  );

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={handleDrawerClose}
        sx={{
          "& .MuiDrawer-paper": {
            width: 500,
            boxSizing: "border-box",
          },
        }}
      >
        <Box
          sx={{
            p: 3,
            height: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 3,
            }}
          >
            <Typography variant="h6" fontWeight="bold">
              {hasExistingMapping ? "Update Mapping" : "New Mapping"} - {selectedApp}
            </Typography>
            <IconButton onClick={handleDrawerClose}>
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Issue Type Selection */}
          <Box sx={{ mb: 3 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Issue Type</InputLabel>
              <Select
                value={selectedIssueType}
                onChange={(e) => handleIssueTypeChange(e.target.value)}
                label="Issue Type"
                disabled={viewMode}
                sx={{
                  backgroundColor: viewMode ? "#f5f5f5" : "white",
                }}
              >
                {availableIssueTypes.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* TestRail Template Selection */}
          {selectedApp.toLowerCase() === 'testrail' && (
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography variant="subtitle2">TestRail Template:</Typography>
                {selectedTemplate ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TemplateIcon color="primary" fontSize="small" />
                    <Typography variant="body2" fontWeight="medium">
                      {selectedTemplate.name}
                    </Typography>
                    <Button 
                      size="small" 
                      onClick={handleClearTemplate}
                      sx={{ minWidth: 'auto', p: 0.5 }}
                    >
                      Change
                    </Button>
                  </Box>
                ) : (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<TemplateIcon />}
                    onClick={handleShowTemplates}
                    disabled={loadingTemplates}
                    sx={{ textTransform: 'none' }}
                  >
                    {loadingTemplates ? 'Loading...' : 'Show Templates'}
                  </Button>
                )}
              </Box>
              {!selectedTemplate && (
                <Typography variant="caption" color="text.secondary">
                  Select a template to see available fields for mapping
                </Typography>
              )}
            </Box>
          )}

          {/* Loading State */}
          {fetchingFields && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
              <CircularProgress size={24} />
              <Typography variant="body2" sx={{ ml: 2 }}>
                Loading fields from {selectedApp}...
              </Typography>
            </Box>
          )}

          {/* Configuration Form */}
          <Box
            sx={{ display: "flex", flexDirection: "column", gap: 3, flex: 1 }}
          >
            {/* Import Mapping Section */}
            <Box>
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    Import Mapping
                  </Typography>
                  {existingImportMapping && !viewMode && (
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      onClick={() => handleDeleteClick('import')}
                      sx={{ textTransform: "none", fontSize: "12px" }}
                    >
                      Delete Import
                    </Button>
                  )}
                </Box>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  {importFields.map((field) => (
                    <Box key={field.key} sx={{ position: "relative" }}>
                      <Typography
                        variant="caption"
                        sx={{
                          position: "absolute",
                          top: -6,
                          left: 12,
                          backgroundColor: "white",
                          px: 0.5,
                          fontSize: "12px",
                          color: "#666",
                          zIndex: 1,
                        }}
                      >
                        {field.label}
                      </Typography>

                      <FormControl fullWidth size="small">
                        <Select
                          value={importFieldMappings[field.key] || ""}
                          onChange={(e) =>
                            handleImportFieldMappingChange(
                              field.key,
                              e.target.value
                            )
                          }
                          displayEmpty
                          disabled={fetchingFields || viewMode || (selectedApp.toLowerCase() === 'testrail' && !selectedTemplate)}
                          sx={{
                            "& .MuiOutlinedInput-notchedOutline": {
                              borderColor: "#ccc",
                            },
                            "& .MuiSelect-select": {
                              padding: "8px 14px",
                              fontSize: "14px",
                            },
                            height: "45px",
                            backgroundColor: viewMode ? "#f5f5f5" : "white",
                          }}
                        >
                          <MenuItem value="">
                            <span style={{ color: "#999" }}>
                              {fetchingFields ? "Loading..." : 
                               selectedApp.toLowerCase() === 'testrail' && !selectedTemplate ? "Select a template first" : 
                               "Select a field"}
                            </span>
                          </MenuItem>
                          {availableTargetFields.map((targetField) => (
                            <MenuItem key={targetField.key} value={targetField.key}>
                              {targetField.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                  ))}

                  {/* Save mapping as inside Import Mapping card */}
                  <Box sx={{ mt: 0.5, position: "relative" }}>
                    <Typography
                      variant="caption"
                      sx={{
                        position: "absolute",
                        top: -6,
                        left: 12,
                        backgroundColor: "white",
                        px: 0.5,
                        fontSize: "10px",
                        color: "#666",
                        zIndex: 1,
                      }}
                    >
                      Save mapping as*
                    </Typography>

                    <TextField
                      value={mappingName}
                      onChange={(e) => setMappingName(e.target.value)}
                      placeholder="Enter a name"
                      fullWidth
                      size="small"
                      disabled={viewMode}
                      sx={{
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#ccc",
                        },
                        "& .MuiOutlinedInput-input": {
                          padding: "8px 14px",
                          fontSize: "14px",
                        },
                        backgroundColor: viewMode ? "#f5f5f5" : "white",
                      }}
                    />
                  </Box>
                </Box>
              </Box>
            </Box>

            {showKeepSameCheckbox && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={keepSameForExport}
                    onChange={(e) => handleKeepSameChange(e.target.checked)}
                    disabled={fetchingFields || viewMode}
                  />
                }
                label="Keep it same for export mapping"
              />
            )}

            {/* Export Mapping Section */}
            <Box>
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    Export Mapping
                  </Typography>
                  {existingExportMapping && !viewMode && (
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      onClick={() => handleDeleteClick('export')}
                      sx={{ textTransform: "none", fontSize: "12px" }}
                    >
                      Delete Export
                    </Button>
                  )}
                </Box>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  {exportFields.map((field) => (
                    <Box key={field.key} sx={{ position: "relative" }}>
                      <Typography
                        variant="caption"
                        sx={{
                          position: "absolute",
                          top: -6,
                          left: 12,
                          backgroundColor: "white",
                          px: 0.5,
                          fontSize: "12px",
                          color: "#666",
                          zIndex: 1,
                        }}
                      >
                        {field.label}
                      </Typography>

                      <FormControl fullWidth size="small">
                        <Select
                          value={exportFieldMappings[field.key] || ""}
                          onChange={(e) =>
                            handleExportFieldMappingChange(
                              field.key,
                              e.target.value
                            )
                          }
                          displayEmpty
                          disabled={keepSameForExport || fetchingFields || viewMode || (selectedApp.toLowerCase() === 'testrail' && !selectedTemplate)}
                          sx={{
                            "& .MuiOutlinedInput-notchedOutline": {
                              borderColor: "#ccc",
                            },
                            "& .MuiSelect-select": {
                              padding: "8px 14px",
                              fontSize: "14px",
                            },
                            height: "45px",
                            backgroundColor: viewMode ? "#f5f5f5" : "white",
                          }}
                        >
                          <MenuItem value="">
                            <span style={{ color: "#999" }}>
                              {fetchingFields ? "Loading..." : 
                               selectedApp.toLowerCase() === 'testrail' && !selectedTemplate ? "Select a template first" : 
                               "Select a field"}
                            </span>
                          </MenuItem>
                          {availableTargetFields.map((targetField) => (
                            <MenuItem key={targetField.key} value={targetField.key}>
                              {targetField.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                  ))}

                  {/* Save mapping as inside Export Mapping card */}
                  <Box sx={{ mt: 0.5, position: "relative" }}>
                    <Typography
                      variant="caption"
                      sx={{
                        position: "absolute",
                        top: -6,
                        left: 12,
                        backgroundColor: "white",
                        px: 0.5,
                        fontSize: "10px",
                        color: "#666",
                        zIndex: 1,
                      }}
                    >
                      Save mapping as*
                    </Typography>

                    <TextField
                      value={
                        keepSameForExport ? mappingName : exportMappingName
                      }
                      onChange={(e) => setExportMappingName(e.target.value)}
                      placeholder="Enter a name"
                      fullWidth
                      size="small"
                      disabled={keepSameForExport || fetchingFields || viewMode}
                      sx={{
                        backgroundColor: viewMode ? "#f5f5f5" : "white",
                        "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#ccc",
                        },
                        "& .MuiOutlinedInput-input": {
                        padding: "8px 14px",
                        fontSize: "14px",
                        },
                        "& .MuiInputBase-input.Mui-disabled": {
                        WebkitTextFillColor: "#000000",
                        backgroundColor: "white",
                      },
                    }}
                    />
                  </Box>
                </Box>
              </Box>
            </Box>

            {loading && (
              <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                <CircularProgress />
              </Box>
            )}
          </Box>

          {/* Action Buttons */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              gap: 1,
              mt: 1,
              pt: 2,
              borderTop: 1,
              borderColor: "divider",
            }}
          >
            <Box sx={{ display: "flex", gap: 1, ml: "auto" }}>
              <Button
                sx={{
                  color: "#666",
                  textTransform: "none",
                  borderColor: "#ddd",
                  mr: 35,
                }}
                variant="outlined"
                onClick={handleDrawerClose}
              >
                Cancel
              </Button>

              {hasExistingMapping ? (
                viewMode ? (
                  <>
                    <Button
                      variant="outlined"
                      startIcon={<EditIcon />}
                      onClick={handleEditMapping}
                      sx={{ textTransform: "none" }}
                    >
                      Edit
                    </Button>
                  </>
                ) : (
                  <Button
                    variant="contained"
                    onClick={handleSaveMapping}
                    disabled={
                      saving ||
                      fetchingFields ||
                      !mappingName.trim() ||
                      !selectedIssueType ||
                      (!keepSameForExport && !exportMappingName.trim())
                    }
                    startIcon={saving ? <CircularProgress size={20} /> : null}
                    sx={{
                      borderRadius: 2,
                      bgcolor: "#49A3FF",
                      "&:hover": {
                        bgcolor: "#369bff",
                      },
                      "&.Mui-disabled": {
                        bgcolor: "#f5f5f5",
                        color: "#ccc",
                      },
                      textTransform: "none",
                      ml: 0
                    }}
                  >
                    {saving ? "Updating..." : "Update"}
                  </Button>
                )
              ) : (
                <Button
                  variant="contained"
                  onClick={handleSaveMapping}
                  disabled={
                    saving ||
                    fetchingFields ||
                    !mappingName.trim() ||
                    !selectedIssueType ||
                    (!keepSameForExport && !exportMappingName.trim())
                  }
                  startIcon={saving ? <CircularProgress size={20} /> : null}
                  sx={{
                    borderRadius: 2,
                    bgcolor: "#49A3FF",
                    "&:hover": {
                      bgcolor: "#369bff",
                    },
                    "&.Mui-disabled": {
                      bgcolor: "#f5f5f5",
                      color: "#ccc",
                    },
                    textTransform: "none",
                  }}
                >
                  {saving ? "Saving..." : "Set Mapping"}
                </Button>
              )}
            </Box>
          </Box>
        </Box>
      </Drawer>

      {/* Template Selection Drawer */}
      <TemplateSelectionDrawer />

      {/* Success Dialog */}
      <Dialog
        open={successDialogOpen}
        onClose={handleCloseSuccessDialog}
        maxWidth="sm"
        PaperProps={{
          sx: {
            m: 0,
            p: 0,
            minHeight: 'unset',
          },
        }}
      >
        <DialogTitle sx={{ pb: 1, pt: 2, px: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CheckCircleIcon sx={{ color: "#4CAF50", fontSize: 24 }} />
            <Typography variant="h6" fontWeight="bold">
              Mapping {(existingImportMapping || existingExportMapping) && viewMode ? "Updated" : "Saved"} Successfully!
            </Typography>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ px: 3, pb: 1 }}>
          <Typography variant="body1">
            Your import & export mapping has been {(existingImportMapping || existingExportMapping) && viewMode ? "updated" : "created"} successfully.
          </Typography>
        </DialogContent>

        <DialogActions sx={{ p: 2, pt: 1 }}>
          <Button
            variant="contained"
            onClick={handleCloseSuccessDialog}
            sx={{
              borderRadius: 2,
              bgcolor: "#49A3FF",
              "&:hover": {
                bgcolor: "#369bff",
              },
              textTransform: "none",
              px: 3,
            }}
          >
            Done
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="sm"
      >
        <DialogTitle>Delete {deleteType === 'import' ? 'Import' : 'Export'} Mapping</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the {deleteType} mapping? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteMapping} 
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
      {/* Delete Success Dialog */}
<Dialog
  open={deleteSuccessDialogOpen}
  onClose={() => setDeleteSuccessDialogOpen(false)}
  maxWidth="sm"
  PaperProps={{
    sx: {
      m: 0,
      p: 0,
      minHeight: 'unset',
    },
  }}
>
  <DialogTitle sx={{ pb: 1, pt: 2, px: 3 }}>
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <CheckCircleIcon sx={{ color: "#4CAF50", fontSize: 24 }} />
      <Typography variant="h6" fontWeight="bold">
        Mapping Deleted Successfully!
      </Typography>
    </Box>
  </DialogTitle>

  <DialogContent sx={{ px: 3, pb: 1 }}>
    <Typography variant="body1">
      {deleteMessage}
    </Typography>
  </DialogContent>

  <DialogActions sx={{ p: 2, pt: 1 }}>
    <Button
      variant="contained"
      onClick={() => setDeleteSuccessDialogOpen(false)}
      sx={{
        borderRadius: 2,
        bgcolor: "#49A3FF",
        "&:hover": {
          bgcolor: "#369bff",
        },
        textTransform: "none",
        px: 3,
      }}
    >
      Done
    </Button>
  </DialogActions>
</Dialog>

{/* Delete Error Dialog */}
 <Dialog
  open={deleteErrorDialogOpen}
  onClose={() => setDeleteErrorDialogOpen(false)}
  maxWidth="sm"
  PaperProps={{
    sx: {
      m: 0,
      p: 0,
      minHeight: 'unset',
    },
  }}
>
  <DialogTitle sx={{ pb: 1, pt: 2, px: 3 }}>
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <Typography variant="h6" fontWeight="bold" color="error">
        Error Deleting Mapping
      </Typography>
    </Box>
  </DialogTitle>

  <DialogContent sx={{ px: 3, pb: 1 }}>
    <Typography variant="body1">
      {deleteMessage}
    </Typography>
  </DialogContent>

  <DialogActions sx={{ p: 2, pt: 1 }}>
    <Button
      variant="contained"
      onClick={() => setDeleteErrorDialogOpen(false)}
      sx={{
        borderRadius: 2,
        bgcolor: "#49A3FF",
        "&:hover": {
          bgcolor: "#369bff",
        },
        textTransform: "none",
        px: 3,
      }}
    >
      Done
    </Button>
  </DialogActions>
 </Dialog>
    </>
  );
};

export default ToolMapping;